import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, PageHeader, Pill, Skeleton, useConfirm } from '../ui';
import { CheckIcon, ChevronDownIcon, LinkIcon, RefreshIcon } from '../ui/icons';
import { sectionCrumb } from '../settings/SectionLayout';
import api from '../config/api';
import NotFound from './NotFound';
import FillFromSources from '../imports/FillFromSources';
import { VOCABULARIES, hasSources, optionLabel, optionValue, presetInfo, uiLang, useKinds, useVocabulary } from '../kinds';
import { errorMessage } from '../errors';
import { invalidateSavedViews } from '../shell/savedViews';

// The models a plain type converts to (backend Kinds/KindRegistry), in the order offered.
const PRESETS = ['movie', 'tv', 'household', 'book'];
const LISTS = ['tags', 'vocab', 'checkbox'];
const TEXTS = ['text', 'textarea'];
const CHOICES = ['select', 'radio', 'checkbox'];
// Targets whose values go through a value map (tags take any word: no map offered here).
const MAPPED = ['select', 'radio', 'checkbox', 'vocab', 'yesno'];
const SCHEMES = ['ean', 'isbn', 'tmdb', 'bgg', 'imdb', 'other'];
const IGNORE = '\u0000ignore';

let uid = 0;
const withUid = (entry) => ({ ...entry, uid: ++uid });
const splitWords = (text) => text.split(';').map(word => word.trim()).filter(Boolean);

/** The field a row's values go to: the model's field, or the owner's (retyped by "as"). */
function targetOf(entry, current, modelByKey) {
  if (entry.to) return modelByKey[entry.to];
  if (!entry.as) return current;
  const field = { ...current, ...entry.as };
  if (!CHOICES.includes(field.type)) delete field.options;
  return field;
}

const separatorApplies = (fromType, toType) =>
  (TEXTS.includes(fromType) && LISTS.includes(toType)) || (['checkbox', 'tags', 'vocab'].includes(fromType) && TEXTS.includes(toType));

/** What "as" needs besides the type, with defaults taken from the field. */
function defaultAs(type, current) {
  const as = { type };
  if (CHOICES.includes(type)) as.options = CHOICES.includes(current.type) ? (current.options || []) : [];
  if (type === 'measure') as.units = ['min'];
  if (type === 'identifier') as.scheme = 'other';
  if (type === 'vocab') as.vocabulary = VOCABULARIES[0];
  if (type === 'money') as.currency = 'EUR';
  if (type === 'rating') as.max = 10;
  return as;
}

/** The options of a new type for one's own field (choices, units, scheme…). */
function AsOptions({ entry, onChange }) {
  const { t } = useTranslation();
  const as = entry.as;
  if (!as) return null;
  const set = (key, value) => onChange({ ...entry, as: { ...as, [key]: value } });
  if (CHOICES.includes(as.type)) {
    return (
      <label className="inline">{t('conversion.asOptions')}
        <input type="text" value={(as.options || []).join('; ')} onChange={(e) => set('options', splitWords(e.target.value))} />
      </label>
    );
  }
  if (as.type === 'measure') {
    return (
      <label className="inline">{t('conversion.asUnits')}
        <input type="text" size={8} value={(as.units || []).join('; ')} onChange={(e) => set('units', splitWords(e.target.value))} />
      </label>
    );
  }
  if (as.type === 'identifier') {
    return (
      <label className="inline">{t('conversion.asScheme')}
        <select value={as.scheme} onChange={(e) => set('scheme', e.target.value)}>
          {SCHEMES.map(scheme => <option key={scheme} value={scheme}>{scheme}</option>)}
        </select>
      </label>
    );
  }
  if (as.type === 'vocab') {
    return (
      <label className="inline">{t('conversion.asVocabulary')}
        <select value={as.vocabulary} onChange={(e) => set('vocabulary', e.target.value)}>
          {VOCABULARIES.map(name => <option key={name} value={name}>{t(`conversion.vocabularies.${name}`, { defaultValue: name })}</option>)}
        </select>
      </label>
    );
  }
  if (as.type === 'money') {
    return (
      <label className="inline">{t('conversion.asCurrency')}
        <input type="text" size={4} maxLength={3} value={as.currency || ''} onChange={(e) => set('currency', e.target.value.toUpperCase())} />
      </label>
    );
  }
  if (as.type === 'rating') {
    return (
      <label className="inline">{t('conversion.asMax')}
        <select value={as.max} onChange={(e) => set('max', Number(e.target.value))}>
          <option value={5}>5</option>
          <option value={10}>10</option>
        </select>
      </label>
    );
  }
  return null;
}

/** Old value → the target's value (an option's code, a genre, yes or no), ignored, or automatic. */
function ValueMap({ entry, target, found, lang, onChange }) {
  const { t } = useTranslation();
  const vocabulary = useVocabulary(target.type === 'vocab' ? target.vocabulary : null);
  const choices = target.type === 'vocab'
    ? vocabulary.map(item => ({ value: item.code, label: item.labels[lang] || item.labels.en }))
    : target.type === 'yesno'
      ? [{ value: 'true', label: t('conversion.yes') }, { value: 'false', label: t('conversion.no') }]
      : (target.options || []).map(option => ({ value: optionValue(option), label: optionLabel(option, lang) }));
  const values = entry.values || {};
  const listed = [...found];
  Object.keys(values).forEach(value => {
    if (!listed.some(item => item.value === value)) listed.push({ value, count: 0 });
  });
  const setValue = (source, raw) => {
    const next = { ...values };
    if (raw === '') delete next[source];
    else if (raw === IGNORE) next[source] = null;
    else next[source] = target.type === 'yesno' ? raw === 'true' : raw;
    onChange({ ...entry, values: next });
  };
  if (!listed.length) return <p className="settings-muted">{t('conversion.noValues')}</p>;
  return (
    <div className="value-map">
      <table>
        <thead>
          <tr><th scope="col">{t('conversion.valueFound')}</th><th scope="col">{t('conversion.valueBecomes')}</th></tr>
        </thead>
        <tbody>
          {listed.map(({ value, count }) => {
            const current = values[value];
            const selected = current === undefined ? '' : current === null ? IGNORE : String(current);
            return (
              <tr key={value}>
                <td>{value} {count > 0 && <span className="settings-muted">({count})</span>}</td>
                <td>
                  <select value={selected} aria-label={t('conversion.valueFor', { value })} onChange={(e) => setValue(value, e.target.value)}>
                    <option value="">{t('conversion.valueAuto')}</option>
                    <option value={IGNORE}>{t('conversion.valueIgnore')}</option>
                    {choices.map(choice => <option key={choice.value} value={choice.value}>{choice.label}</option>)}
                  </select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Counts of one row of a preview: converted, unchanged, dropped (with examples), empty. */
function Counts({ line }) {
  const { t } = useTranslation();
  if (!line) return <span className="settings-muted">—</span>;
  const examples = [...(line.examples || []), ...(line.entry_examples || [])];
  return (
    <div className="conversion-counts">
      <span>{t('conversion.counts.converted', { count: line.converted })}</span>
      <span>{t('conversion.counts.unchanged', { count: line.unchanged })}</span>
      <span className={line.dropped || line.entries_dropped ? 'dropped' : undefined}>
        {t('conversion.counts.dropped', { count: line.dropped })}
        {line.entries_dropped > 0 && ` · ${t('conversion.counts.entriesDropped', { count: line.entries_dropped })}`}
      </span>
      <span>{t('conversion.counts.empty', { count: line.empty })}</span>
      {examples.length > 0 && (
        <details>
          <summary>{t('conversion.examples')}</summary>
          <ul>{examples.map((example, index) => <li key={index}><code>{example}</code></li>)}</ul>
        </details>
      )}
    </div>
  );
}

/**
 * /settings/item-types/:id/convert — turns a plain type into a model's
 * (film, TV series, household item): each current field goes to a field of
 * the model or stays the owner's own (optionally retyped), values are mapped
 * where codes are expected, a preview counts what converts, and the
 * conversion keeps a backup of every item (items_pre_kind_backup).
 */
function ItemTypeConversion({ itemTypes, loaded = true, onConverted }) {
  const { t, i18n } = useTranslation();
  const confirm = useConfirm();
  const lang = uiLang(i18n);
  const { id } = useParams();
  const navigate = useNavigate();
  const itemType = (itemTypes || []).find(type => String(type.id) === String(id));
  const [preset, setPreset] = useState(null);
  const [data, setData] = useState(null);
  const [mapping, setMapping] = useState([]);
  const [unused, setUnused] = useState([]);
  const [namesLang, setNamesLang] = useState(lang);
  const [preview, setPreview] = useState(null);
  const [stale, setStale] = useState(false);
  const [busy, setBusy] = useState(null);   // 'proposal' | 'preview' | 'convert'
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);   // {text, index?}
  const [done, setDone] = useState(null);
  const [openValues, setOpenValues] = useState({});
  const [resume, setResume] = useState(undefined);   // a conversion left unfinished: undefined loading, null none
  const kinds = useKinds();
  const unfinished = !!itemType?.conversion_running;

  // A conversion left unfinished is shown with the choices it was started with, to be continued.
  useEffect(() => {
    if (!unfinished) return undefined;
    let alive = true;
    api.get(`/item-types/${id}/convert`)
      .then(response => { if (alive) setResume(response.data.conversion); })
      .catch(() => { if (alive) setResume(null); });
    return () => { alive = false; };
  }, [id, unfinished]);

  const modelByKey = useMemo(() => Object.fromEntries((data?.model || []).map(field => [field.key, field])), [data]);
  const currentByKey = useMemo(() => Object.fromEntries((data?.item_type?.fields || []).map(field => [field.key, field])), [data]);
  const lines = useMemo(() => Object.fromEntries((preview?.entries || []).map(line => [line.from, line])), [preview]);

  // Still loading the types: a placeholder; loaded without this one (or none at all): not found.
  if (!loaded && !itemType) return <Skeleton height={320} radius="var(--radius-lg)" />;
  if (!itemType || !itemType.is_mine) return <NotFound />;

  const errorText = (err) => {
    const body = err?.response?.data || {};
    if (body.code === 'invalid_mapping' && Number.isInteger(body.index)) {
      return { index: body.index, text: t('conversion.errors.row', { field: currentByKey[mapping[body.index]?.from]?.name || '', error: body.error }) };
    }
    if (body.code) return { text: t(`conversion.errors.${body.code}`, { defaultValue: errorMessage(t, err), error: body.error || '' }) };
    return { text: t('conversion.errors.generic', { error: errorMessage(t, err) }) };
  };

  const choose = async (next) => {
    setPreset(next);
    setBusy('proposal');
    setError(null);
    setData(null);
    setPreview(null);
    try {
      const response = await api.get(`/item-types/${id}/convert/proposal`, { params: { preset: next, lang } });
      setData(response.data);
      setMapping(response.data.proposal.mapping.map(withUid));
      setUnused(response.data.proposal.unused);
      setPreview(response.data.preview);
      setStale(false);
      setOpenValues({});
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  const request = () => ({
    preset,
    lang: namesLang,
    unused,
    mapping: mapping.map(({ uid: _uid, ...entry }) => {
      const out = { from: entry.from, to: entry.to || null };
      if (!entry.to && entry.as) out.as = entry.as;
      if (entry.values && Object.keys(entry.values).length) out.values = entry.values;
      if (entry.separator) out.separator = entry.separator;
      return out;
    }),
  });

  const change = (row, next) => {
    setMapping(prev => prev.map(entry => (entry.uid === row.uid ? next : entry)));
    setStale(true);
    setError(null);
  };

  const runPreview = async () => {
    setBusy('preview');
    setError(null);
    try {
      const response = await api.post(`/item-types/${id}/convert/preview`, request());
      setPreview(response.data);
      setStale(false);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  const continueConversion = async () => {
    setBusy('convert');
    setError(null);
    try {
      let answer;
      do {
        // eslint-disable-next-line no-await-in-loop
        answer = (await api.post(`/item-types/${id}/convert`, { resume: true })).data;
        const { processed, total } = answer;
        setResume(prev => ({ ...prev, processed, total }));
      } while (!answer.done);
      setDone(answer);
      invalidateSavedViews();
      if (onConverted) onConverted();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  const convert = async () => {
    const model = PRESETS.includes(preset) ? t(`conversion.presets.${preset}`) : preset;
    if (!(await confirm({
      title: t('conversion.confirmTitle', { name: data.item_type.name }),
      message: t('conversion.confirm', { count: data.item_count, name: data.item_type.name, model }),
      confirmLabel: t('conversion.confirmButton', { model }),
    }))) return;
    setBusy('convert');
    setError(null);
    setProgress({ processed: 0, total: data.item_count });
    try {
      // Batches run on the server; a large type answers before it is done and is sent again until it is.
      let answer;
      do {
        // eslint-disable-next-line no-await-in-loop
        answer = (await api.post(`/item-types/${id}/convert`, request())).data;
        setProgress({ processed: answer.processed, total: answer.total });
      } while (!answer.done);
      setDone(answer);
      if (onConverted) onConverted();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  const taken = (except) => new Set(mapping.filter(entry => entry.uid !== except && entry.to).map(entry => entry.to));
  const fieldLabel = (field) => (field?.labels ? (field.labels[lang] || field.name) : field?.name);
  const typeLabel = (type) => t(`fieldType.${type}`, { defaultValue: type });
  /** A code a warning names, as the model's field shows it (an option's label; a genre stays its code). */
  const valueLabel = (from, code) => {
    const entry = mapping.find(row => row.from === from);
    const option = (modelByKey[entry?.to]?.options || []).find(o => optionValue(o) === code);
    return option ? optionLabel(option, lang) : code;
  };

  // The names the owner knew their fields by (before the conversion).
  const oldFields = data?.item_type?.fields || resume?.old_fields || [];
  const nameOf = (key) => oldFields.find(field => field.key === key)?.name || key;
  const modelFieldsOf = (presetId) => presetInfo(kinds, presetId)?.preset?.fields || [];

  const crumbs = (label) => [sectionCrumb(t, 'settings'), { label: t('nav.itemTypes'), to: '/settings/item-types' }, { label }];
  const presetName = (id) => (PRESETS.includes(id) ? t(`conversion.presets.${id}`) : id);
  /** Where the owner is: 1 model, 2 fields, 3 preview and convert, 4 done. */
  const Steps = ({ current }) => (
    <ol className="stepper" aria-label={t('conversion.steps.label')}>
      {['model', 'fields', 'convert', 'done'].map((step, index) => {
        const number = index + 1;
        const state = number < current ? 'is-done' : number === current ? 'is-current' : '';
        return (
          <li key={step} className={`stepper__step ${state}`} aria-current={number === current ? 'step' : undefined}>
            <span className="stepper__number" aria-hidden="true">{number < current ? <CheckIcon size={14} /> : number}</span>
            <span className="stepper__label">{t(`conversion.steps.${step}`)}</span>
          </li>
        );
      })}
    </ol>
  );

  if (done) {
    return (
      <div className="conversion-page native-controls">
        <PageHeader breadcrumb={crumbs(done.item_type.name)} title={t('conversion.doneTitle', { name: done.item_type.name })} />
        <Steps current={4} />
        <div className="settings-stack">
          <Alert tone="success" title={t('conversion.done', { count: done.processed })}>{t('conversion.backupKept')}</Alert>
          <Card padding="lg">
            <div className="data-table-wrap">
              <table role="table" className="data-table data-table--stack">
                <thead><tr role="row"><th role="columnheader" scope="col">{t('conversion.field')}</th><th role="columnheader" scope="col">{t('conversion.target')}</th><th role="columnheader" scope="col">{t('conversion.result')}</th></tr></thead>
                <tbody>
                  {done.report.entries.map(line => (
                    <tr role="row" key={line.from}>
                      <th role="rowheader" scope="row">{nameOf(line.from)} <code className="settings-code">{line.from}</code></th>
                      <td role="cell" data-label={t('conversion.target')}>{line.to ? fieldLabel(done.item_type.fields.find(field => field.key === line.to)) : t('conversion.ownField')}</td>
                      <td role="cell" data-label={t('conversion.result')}><Counts line={line} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          {hasSources(done.item_type.preset, kinds) && <FillFromSources typeId={done.item_type.id} />}
          <div className="settings-actions">
            <Button onClick={() => navigate('/settings/item-types')}>{t('conversion.toTypes')}</Button>
            {hasSources(done.item_type.preset, kinds) && (
              <Button as={Link} variant="secondary" icon={LinkIcon} to={`/settings/item-types/${id}/integration`}>{t('itemTypes.integration')}</Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (unfinished) {
    if (resume === undefined) return <Skeleton height={320} radius="var(--radius-lg)" />;
    if (resume === null) return <NotFound />;
    const model = modelFieldsOf(resume.preset);
    const modelLabel = (key) => model.find(field => field.key === key)?.labels?.[lang] || key;
    return (
      <div className="conversion-page native-controls">
        <PageHeader breadcrumb={crumbs(itemType.name)} title={t('conversion.resumeTitle', { name: itemType.name })} subtitle={t('conversion.resumeIntro')} />
        <div className="settings-stack">
          <Card padding="lg">
            <h2 className="settings-card__title">{t('conversion.resumeModel', { model: presetName(resume.preset) })}</h2>
            <div className="data-table-wrap">
              <table role="table" className="data-table data-table--stack">
                <thead><tr role="row"><th role="columnheader" scope="col">{t('conversion.field')}</th><th role="columnheader" scope="col">{t('conversion.target')}</th></tr></thead>
                <tbody>
                  {(resume.request?.mapping || []).map(entry => (
                    <tr role="row" key={entry.from}>
                      <th role="rowheader" scope="row">{nameOf(entry.from)} <code className="settings-code">{entry.from}</code></th>
                      <td role="cell" data-label={t('conversion.target')}>
                        {entry.to ? modelLabel(entry.to) : t('conversion.ownField')}
                        {!entry.to && entry.as && <span className="settings-muted"> · {typeLabel(entry.as.type)}</span>}
                        {entry.separator && <span className="settings-muted"> · {t('conversion.splitOn')} <code>{entry.separator}</code></span>}
                        {entry.values && Object.keys(entry.values).length > 0 && (
                          <span className="settings-muted"> · {t('conversion.values')} ({Object.keys(entry.values).length})</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(resume.request?.unused || []).length > 0 && (
              <p className="settings-muted conversion-page__unused">{t('conversion.unusedList', { fields: resume.request.unused.map(modelLabel).join(', ') })}</p>
            )}
          </Card>
          <div className="conversion-progress" role="status">
            <progress max={resume.total || 1} value={resume.processed} />
            <span>
              {busy === 'convert'
                ? t('conversion.progress', { processed: resume.processed, total: resume.total })
                : t('conversion.resumeProgress', { processed: resume.processed, total: resume.total })}
            </span>
          </div>
          {error && <Alert tone="danger">{error.text}</Alert>}
          <div className="settings-actions">
            <Button icon={RefreshIcon} onClick={continueConversion} disabled={!!busy} loading={busy === 'convert'}>
              {busy === 'convert' ? t('conversion.converting') : t('conversion.continueButton')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (itemType.preset) {
    return (
      <div className="conversion-page">
        <PageHeader breadcrumb={crumbs(itemType.name)} title={itemType.name} />
        <Alert tone="info">{t('conversion.alreadyModel', { name: itemType.name })}</Alert>
      </div>
    );
  }

  const addable = (data?.model || []).filter(field => !mapping.some(entry => entry.to === field.key));
  const step = !data ? 1 : (stale || !preview) ? 2 : 3;

  return (
    <div className="conversion-page native-controls">
      <PageHeader breadcrumb={crumbs(itemType.name)} title={t('conversion.title', { name: itemType.name })} subtitle={t('conversion.intro', { name: itemType.name })} />
      <Steps current={step} />

      <div className="settings-stack">
        <Card as="section" padding="lg" aria-labelledby="conversion-model">
          <h2 id="conversion-model" className="settings-card__title">{t('conversion.chooseModel')}</h2>
          <div className="conversion-presets" role="radiogroup" aria-labelledby="conversion-model">
            {PRESETS.map(model => (
              <label key={model} className={`conversion-preset${preset === model ? ' is-selected' : ''}`}>
                <input type="radio" name="preset" value={model} checked={preset === model} disabled={!!busy} onChange={() => choose(model)} />
                <span className="conversion-preset__text">
                  <span className="conversion-preset__title">{t(`conversion.presets.${model}`)}</span>
                  <span className="conversion-preset__hint">{t(`conversion.presetHelp.${model}`)}</span>
                </span>
              </label>
            ))}
          </div>
        </Card>

        {busy === 'proposal' && <p className="settings-muted" role="status">{t('conversion.loading')}</p>}
        {error && <Alert tone="danger">{error.text}</Alert>}

        {data && (
          <>
            <Alert tone="info" role="note">{t('conversion.inPlace', {
              count: data.item_count, name: data.item_type.name, model: presetName(preset),
            })}</Alert>
            <Card as="section" padding="lg" aria-labelledby="conversion-fields">
              <h2 id="conversion-fields" className="settings-card__title">{t('conversion.fields')}</h2>
              <p className="settings-muted">{t('conversion.fieldsHelp', { count: data.item_count })}</p>
              {data.warnings.length > 0 && (
                <Alert tone="warning" role="note" title={t('conversion.warningsTitle')} className="conversion-page__warnings">
                  <ul className="alert-list">
                    {data.warnings.map((warning, index) => (
                      <li key={index}>{t(`conversion.warnings.${warning.code}`, {
                        field: currentByKey[warning.from]?.name || warning.from || '',
                        value: warning.value ?? '',
                        to: warning.to != null ? valueLabel(warning.from, warning.to) : '',
                        count: warning.count ?? 0,
                        examples: (warning.examples || []).join(', '),
                        fields: (warning.unused || []).map(key => fieldLabel(modelByKey[key])).join(', '),
                      })}</li>
                    ))}
                  </ul>
                </Alert>
              )}
              <div className="data-table-wrap">
                <table role="table" className="data-table data-table--stack conversion-table">
                  <thead>
                    <tr role="row">
                      <th role="columnheader" scope="col">{t('conversion.field')}</th>
                      <th role="columnheader" scope="col">{t('conversion.target')}</th>
                      <th role="columnheader" scope="col">{t('conversion.preview')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mapping.map((entry, index) => {
                      const current = currentByKey[entry.from] || { key: entry.from, name: entry.from, type: 'text' };
                      const target = targetOf(entry, current, modelByKey);
                      const used = taken(entry.uid);
                      const compatible = data.compatible[entry.from] || [];
                      return (
                        <tr role="row" key={entry.uid} className={error?.index === index ? 'row-error' : undefined}>
                          <th role="rowheader" scope="row">
                            {current.name}
                            <span className="integration-table__shape"><code>{current.key}</code> · {typeLabel(current.type)}</span>
                          </th>
                          <td role="cell" data-label={t('conversion.target')}>
                            <select value={entry.to || ''} aria-label={t('conversion.targetFor', { field: current.name })} disabled={!!busy}
                              onChange={(e) => change(entry, { uid: entry.uid, from: entry.from, to: e.target.value || null })}>
                              <option value="">{t('conversion.ownField')}</option>
                              {compatible.map(key => (
                                <option key={key} value={key} disabled={used.has(key)}>
                                  {fieldLabel(modelByKey[key])} ({typeLabel(modelByKey[key]?.type)}){used.has(key) ? ` — ${t('conversion.taken')}` : ''}
                                </option>
                              ))}
                            </select>
                            {!entry.to && (
                              <div className="transform-controls conversion-table__as">
                                <label className="inline">{t('conversion.asType')}
                                  <select value={entry.as?.type || current.type} disabled={!!busy}
                                    onChange={(e) => change(entry, { ...entry, values: undefined,
                                      as: e.target.value === current.type ? undefined : defaultAs(e.target.value, current) })}>
                                    {(data.retypes[entry.from] || [current.type]).map(type => (
                                      <option key={type} value={type}>{type === current.type ? t('conversion.sameType', { type: typeLabel(type) }) : typeLabel(type)}</option>
                                    ))}
                                  </select>
                                </label>
                                <AsOptions entry={entry} onChange={(next) => change(entry, next)} />
                              </div>
                            )}
                            {target && separatorApplies(current.type, target.type) && (
                              <label className="inline conversion-table__separator">
                                {t(TEXTS.includes(target.type) ? 'conversion.joinWith' : 'conversion.splitOn')}
                                <input type="text" size={4} maxLength={10} value={entry.separator || ''} disabled={!!busy}
                                  className="native-controls__narrow"
                                  placeholder={TEXTS.includes(target.type) ? '; ' : '; , |'}
                                  onChange={(e) => change(entry, { ...entry, separator: e.target.value || undefined })} />
                              </label>
                            )}
                            {target && MAPPED.includes(target.type) && (entry.to || entry.as) && (
                              <div>
                                <Button variant="ghost" size="sm" iconRight={ChevronDownIcon} aria-expanded={!!openValues[entry.uid]}
                                  className={`integration-values-toggle${openValues[entry.uid] ? ' is-open' : ''}`}
                                  onClick={() => setOpenValues(prev => ({ ...prev, [entry.uid]: !prev[entry.uid] }))}>
                                  {t('conversion.values')}{entry.values && Object.keys(entry.values).length > 0 && ` (${Object.keys(entry.values).length})`}
                                </Button>
                                {openValues[entry.uid] && (
                                  <ValueMap entry={entry} target={target} lang={lang} found={lines[entry.from]?.found || []}
                                    onChange={(next) => change(entry, next)} />
                                )}
                              </div>
                            )}
                            {error?.index === index && <p className="ui-field__error">{error.text}</p>}
                          </td>
                          <td role="cell" data-label={t('conversion.preview')}><Counts line={stale ? null : lines[entry.from]} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>

            <Card as="section" padding="lg" aria-labelledby="conversion-added">
              <h2 id="conversion-added" className="settings-card__title">{t('conversion.added')}</h2>
              <p className="settings-muted">{t('conversion.addedHelp')}</p>
              {addable.length === 0 ? <p className="settings-muted">{t('conversion.noneAdded')}</p> : (
                <ul className="conversion-added">
                  {addable.map(field => (
                    <li key={field.key}>
                      <label className="choice-set__option">
                        <input type="checkbox" checked={unused.includes(field.key)} disabled={!!busy || field.required}
                          onChange={(e) => { setUnused(prev => (e.target.checked ? [...prev, field.key] : prev.filter(key => key !== field.key))); setStale(true); }} />
                        <span>{fieldLabel(field)} <span className="settings-muted">({typeLabel(field.type)})</span></span>
                        {unused.includes(field.key) && <Pill>{t('conversion.unused')}</Pill>}
                      </label>
                    </li>
                  ))}
                </ul>
              )}
              <div className="conversion-page__names">
                <label className="ui-field__label" htmlFor="conversion-names">{t('conversion.namesLanguage')}</label>
                <select id="conversion-names" value={namesLang} disabled={!!busy} aria-describedby="conversion-names-hint"
                  onChange={(e) => { setNamesLang(e.target.value); setStale(true); }}>
                  <option value="fr">{t('integration.languages.fr')}</option>
                  <option value="en">{t('integration.languages.en')}</option>
                </select>
                <p id="conversion-names-hint" className="ui-field__hint">{t('conversion.namesLanguageHelp')}</p>
              </div>
            </Card>

            <div className="item-form__bar conversion-bar">
              <span className="item-form__status" aria-live="polite">
                {stale ? t('conversion.stale')
                  : preview ? t('conversion.previewSummary', { count: preview.items, renamed: Object.keys(preview.renamed || {}).length }) : ''}
              </span>
              <Button variant="secondary" icon={RefreshIcon} onClick={runPreview} disabled={!!busy} loading={busy === 'preview'}>
                {busy === 'preview' ? t('conversion.previewing') : t('conversion.previewButton')}
              </Button>
              <Button onClick={convert} disabled={!!busy || stale || !preview} loading={busy === 'convert'}
                title={stale || !preview ? t('conversion.convertNeedsPreview') : undefined}
                aria-describedby={stale || !preview ? 'conversion-needs-preview' : undefined}>
                {busy === 'convert' ? t('conversion.converting') : t('conversion.convertButton')}
              </Button>
              {(stale || !preview) && <span id="conversion-needs-preview" className="visually-hidden">{t('conversion.convertNeedsPreview')}</span>}
            </div>
            {busy === 'convert' && progress && (
              <div className="conversion-progress" role="status">
                <progress max={progress.total || 1} value={progress.processed} />
                <span>{t('conversion.progress', { processed: progress.processed, total: progress.total })}</span>
              </div>
            )}
            {preview && !stale && Object.keys(preview.renamed || {}).length > 0 && (
              <p className="settings-muted">{t('conversion.renamed', { list: Object.entries(preview.renamed).map(([from, to]) => `${from} → ${to}`).join(', ') })}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ItemTypeConversion;
